import { useMemo, useState } from "react";
import { Play } from "lucide-react";
import { More } from "components/field/radarUi";
import { duration } from "utils/fieldFormat";
import { formatPumpDuration, pumpDurationSeconds } from "utils/pumpFormat";
import {
  bandOf,
  bandText,
  finiteNumber,
  formatCm,
  formatSignedCm,
  groupByTier,
  isTargetPlan,
  minutesText,
  offsetText,
  pacingText,
  pumpSecondsPerCm,
  planText,
  planTimeline,
  plannedLevels,
  presetBlockers,
  rulerPlan,
  signedCm,
  startSummary,
  stepSeconds,
  stepText,
  targetPlanText,
} from "utils/radarLab";

const TIER_TITLES = {
  0: "Tier 0 · smoke test",
  1: "Tier 1 · staircase",
  2: "Tier 2 · benchmark",
  3: "Tier 3 · fine steps",
};

const readCm = (text) => finiteNumber(String(text ?? "").trim().replace(",", "."));

// The band a zone preset steps through: its own, or two levels typed in
// (checked against the tank bounds here; the server checks the rest).
function readBand(lowText, highText, limits) {
  const low = readCm(lowText);
  const high = readCm(highText);
  const min = finiteNumber(limits?.tank_min_cm);
  const max = finiteNumber(limits?.tank_max_cm);
  if (low === null || high === null || low >= high)
    return { band: null, error: "Enter the band as two levels in cm, the lower first." };
  if ((min !== null && low < min) || (max !== null && high > max))
    return { band: null, error: `The band must lie within the tank bounds, ${formatCm(min)} to ${formatCm(max)}.` };
  return { band: [low, high], error: "" };
}

// Pick a preset (grouped by tier), see the plan it would run from the level
// now, and start it after confirming what will happen. A target preset's
// steps aim at levels on the ruler; a sweep that runs in two parts asks
// which, a zone takes a band.
// A plan of up to this many steps shows them at once; a longer one (a sweep, a
// band of fine steps) keeps them folded under its summary.
const PLAN_OPEN_UP_TO = 8;

export default function LabPresets({ status, ruler, rulerValid, disabled, onStart }) {
  const presets = useMemo(() => status?.presets || [], [status]);
  const groups = useMemo(() => groupByTier(presets), [presets]);
  const [chosen, setChosen] = useState("");
  const preset =
    presets.find((p) => p.key === chosen) ||
    presets.find((p) => p.can_start) ||
    presets[0] ||
    null;
  const [notes, setNotes] = useState("");
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [part, setPart] = useState(1);
  // The band as typed, for one preset (null until changed: its own band).
  const [bandInput, setBandInput] = useState(null);

  const limits = status?.limits || {};
  const factors = status?.calibration || null;
  const derived = status?.level?.ok ? finiteNumber(status?.level?.level_cm) : null;
  const usingRuler = rulerValid && finiteNumber(ruler) !== null;
  const start = usingRuler ? finiteNumber(ruler) : derived;
  const target = isTargetPlan(preset);
  const parts = Math.max(1, Math.round(finiteNumber(preset?.parts) ?? 1));
  const chosenPart = target && parts > 1 ? Math.min(part, parts) : 1;
  const ownBand = target ? bandOf(preset) : null;
  const typed = ownBand && bandInput?.key === preset?.key ? bandInput : null;
  const bandRead = typed ? readBand(typed.low, typed.high, limits) : { band: ownBand, error: "" };
  const bandChanged =
    !!ownBand && !!bandRead.band && (Math.abs(bandRead.band[0] - ownBand[0]) > 1e-9 || Math.abs(bandRead.band[1] - ownBand[1]) > 1e-9);
  // The status previews a target preset's part 1, at its own band, from the
  // derived level; anything else the server works out when it starts.
  const otherPlan = target && (chosenPart !== 1 || bandChanged);
  const unplanned = target && (usingRuler || otherPlan);
  // A ruler reading replaces the derived level: a step preset's plan is
  // oriented and checked from it here, as the server will (a target
  // preset's is worked out by the server when it starts).
  const fromRuler = usingRuler && preset && !target ? rulerPlan(preset, start, limits, factors) : null;
  const steps = fromRuler ? fromRuler.steps : preset?.steps || [];
  const turned = !!fromRuler && fromRuler.mirrored !== !!preset?.mirrored;
  const showPlan = steps.length > 0 && !otherPlan;
  const levels = usingRuler
    ? plannedLevels(start, steps, factors)
    : preset?.fits?.levels || plannedLevels(start, steps, factors);
  const timeline = planTimeline({ ...preset, steps }, limits);
  const rulerFitOf = (p) => (usingRuler && !isTargetPlan(p) ? rulerPlan(p, start, limits, factors).fit : null);
  const blockersOf = (p, own = false) =>
    presetBlockers(p, status, {
      rulerFit: rulerFitOf(p),
      ruler: usingRuler,
      unplanned: isTargetPlan(p) && (usingRuler || (own && otherPlan)),
    });
  const blockers = [...blockersOf(preset, true), ...(bandRead.error ? [bandRead.error] : [])];
  const capture = status?.capture_plans?.[preset?.capture_plan] || null;
  const canStart = !!preset && !disabled && !blockers.length && !busy;
  const oneCm = stepSeconds({ cm: 1 }, { spacing_s: 0 }, limits);
  // The pump time per step at the server's rate (the same one stepSeconds plans with).
  const pumpRate = { seconds_per_cm: pumpSecondsPerCm(limits) };
  const floor = finiteNumber(limits.step_spacing_min_s) ?? 0;
  const ceiling = finiteNumber(limits.tank_ceiling_cm);
  const deepest = finiteNumber(limits.max_drain_cm);
  const tolerance = finiteNumber(limits.target_tolerance_cm) ?? 0.06;

  const confirmStart = async () => {
    setBusy(true);
    setError(null);
    try {
      await onStart({
        preset: preset.key,
        notes,
        rulerLevelCm: usingRuler ? start : null,
        ...(target && parts > 1 ? { part: chosenPart } : {}),
        ...(bandChanged ? { band: bandRead.band } : {}),
      });
      setAsking(false);
      setNotes("");
    } catch (e) {
      const message = e.message || "The lab run could not start.";
      // A single problem is also the server's detail: said once.
      setError({ message, problems: (e.data?.problems || []).filter((p) => p !== message) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="field-panel" aria-labelledby="lab-presets-title">
      <div className="field-panel-head">
        <div>
          <h2 id="lab-presets-title">Start a lab run</h2>
          <p>Every plan returns to its starting level, one pump step at a time.</p>
        </div>
      </div>
      <div className="field-panel-body">
        <More label="The rules every plan follows" className="is-why field-lab-rules">
          <p className="field-caption" style={{ margin: 0 }}>
            Every plan returns to its starting level, one pump step at a time,{" "}
            {floor > 0
              ? `at least ${duration(floor)} apart.`
              : `each as fast as the engine allows (${formatPumpDuration(oneCm)} for a 1 cm step).`}
            {ceiling !== null && ` No fill that could pass the tank ceiling of ${formatCm(ceiling, 2)} is planned or pumped.`}
            {deepest !== null && ` No plan takes the water more than ${formatCm(deepest)} below its start.`}
          </p>
        </More>
        {disabled && (
          <div className="field-notice" style={{ marginBottom: 14 }}>
            A lab run is in progress: a new one can start after it ends.
          </div>
        )}
        {groups.map((group) => (
          <div key={group.tier} className="field-lab-tier-group">
            <h3>{TIER_TITLES[group.tier] || `Tier ${group.tier}`}</h3>
            <div className="field-lab-presets">
              {group.presets.map((p) => {
                const blocked = (p.key === preset?.key ? blockers : blockersOf(p)).length > 0;
                return (
                  <button
                    key={p.key}
                    type="button"
                    className={`field-lab-preset${preset?.key === p.key ? " is-selected" : ""}${blocked ? " is-blocked" : ""}`}
                    aria-pressed={preset?.key === p.key}
                    onClick={() => {
                      setChosen(p.key);
                      setAsking(false);
                      setError(null);
                      setPart(1);
                      setBandInput(null);
                    }}
                  >
                    <b>{p.label || p.key}</b>
                    <span>{isTargetPlan(p) ? targetPlanText(p) : planText(p.steps)}</span>
                    <small>
                      {isTargetPlan(p) && !(p.steps || []).length
                        ? "Its length once the level is known"
                        : `${duration(p.duration_s ?? 0)} · ${minutesText(p.pump_s)} of pumping`}
                      {p.unlocked === false ? " · locked" : blocked ? " · cannot start now" : ""}
                    </small>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        {!presets.length && <p className="field-caption">No presets from the server yet.</p>}
        {preset && (
          <div className="field-lab-preview" aria-label="Plan preview">
            <div>
              <b>{preset.label || preset.key}</b>
              {preset.description && <p className="field-caption" style={{ marginTop: 4 }}>{preset.description}</p>}
            </div>
            {target && parts > 1 && (
              <fieldset className="field-lab-choice">
                <legend>Part</legend>
                <div className="field-lab-row">
                  {Array.from({ length: parts }, (_, i) => i + 1).map((n) => (
                    <label key={n}>
                      <input
                        type="radio"
                        name="lab-part"
                        value={n}
                        checked={chosenPart === n}
                        onChange={() => {
                          setPart(n);
                          setAsking(false);
                        }}
                      />{" "}
                      Part {n} of {parts}
                    </label>
                  ))}
                </div>
                <small className="field-caption" style={{ marginTop: 0 }}>
                  This sweep takes more steps than one lab run may
                  {finiteNumber(limits.steps_max) !== null ? ` (${limits.steps_max} at most)` : ""}, so it runs as {parts} lab
                  runs, each back at its start: run part 1, then part 2.
                </small>
              </fieldset>
            )}
            {ownBand && (
              <fieldset className="field-lab-choice">
                <legend>Band (cm on the ruler)</legend>
                <div className="field-lab-row">
                  <input
                    aria-label="Band from (cm)"
                    inputMode="decimal"
                    value={typed ? typed.low : String(ownBand[0])}
                    aria-invalid={bandRead.error ? "true" : "false"}
                    onChange={(e) => {
                      setBandInput({ key: preset.key, low: e.target.value, high: typed ? typed.high : String(ownBand[1]) });
                      setAsking(false);
                    }}
                  />
                  <span>to</span>
                  <input
                    aria-label="Band to (cm)"
                    inputMode="decimal"
                    value={typed ? typed.high : String(ownBand[1])}
                    aria-invalid={bandRead.error ? "true" : "false"}
                    onChange={(e) => {
                      setBandInput({ key: preset.key, low: typed ? typed.low : String(ownBand[0]), high: e.target.value });
                      setAsking(false);
                    }}
                  />
                  {typed && (
                    <button type="button" className="field-button" onClick={() => setBandInput(null)}>
                      Back to {bandText(ownBand)}
                    </button>
                  )}
                </div>
                <small className="field-caption" style={{ marginTop: 0 }}>
                  {bandRead.error ||
                    `The preset's own band is ${bandText(ownBand)}. The server refuses a band it cannot reach safely: outside the tank bounds, ${deepest !== null ? `more than ${formatCm(deepest)}` : "too far"} below the start, or with a fill that could pass the ceiling.`}
                </small>
              </fieldset>
            )}
            <p className="field-caption" style={{ marginTop: 0 }}>
              Starting from {start === null ? "an unknown level" : formatCm(start)}
              {usingRuler ? " (your ruler reading)" : start === null ? "" : " (derived from the node)"}
              {showPlan
                ? ` · about ${duration(preset.duration_s ?? timeline.end)} in all, ${minutesText(preset.pump_s)} of pumping.`
                : "."}
            </p>
            {turned && (
              <p className="field-caption" style={{ marginTop: 0 }}>
                From your ruler reading the server takes{" "}
                {fromRuler.mirrored ? "its mirror: it drains first" : "it as written: it fills first"}.
              </p>
            )}
            {unplanned && (
              <p className="field-notice" role="note" style={{ margin: 0 }}>
                {otherPlan
                  ? `The server works out ${bandChanged ? `the steps through ${bandText(bandRead.band)}` : `part ${chosenPart}'s steps`} from the tank's level when it starts, and refuses them if they do not fit the tank; the lab run lists every one as soon as it starts.`
                  : steps.length
                    ? "From your ruler reading the server works the steps out again when it starts (and refuses them if they do not fit the tank): the ones below are from the derived level."
                    : "From your ruler reading the server works the steps out when it starts, and refuses them if they do not fit the tank."}
              </p>
            )}
            {showPlan && (
              <More
                label={`The ${steps.length} planned step${steps.length === 1 ? "" : "s"}`}
                open={steps.length <= PLAN_OPEN_UP_TO}
              >
                {target && (
                  <table className="field-table field-lab-plan">
                    <thead>
                      <tr>
                        <th scope="col">Step</th>
                        <th scope="col">Aim at</th>
                        <th scope="col">Command</th>
                        <th scope="col">Pump</th>
                        <th scope="col">Starts</th>
                      </tr>
                    </thead>
                    <tbody>
                      {steps.map((step, i) => (
                        <tr key={i}>
                          <td>
                            {i + 1} of {steps.length}
                          </td>
                          <td>{formatCm(step.target_cm, 2)}</td>
                          <td>{stepText(step)}</td>
                          <td>{formatPumpDuration(pumpDurationSeconds(Math.abs(signedCm(step)), pumpRate))}</td>
                          <td>{offsetText(timeline.starts[i])}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {!target && (
                  <table className="field-table field-lab-plan">
                    <thead>
                      <tr>
                        <th scope="col">Step</th>
                        <th scope="col">Move</th>
                        <th scope="col">Pump</th>
                        <th scope="col">Level after</th>
                        <th scope="col">Starts</th>
                      </tr>
                    </thead>
                    <tbody>
                      {steps.map((step, i) => (
                        <tr key={i}>
                          <td>
                            {i + 1} of {steps.length}
                          </td>
                          <td>{formatSignedCm(signedCm(step))}</td>
                          <td>{formatPumpDuration(pumpDurationSeconds(Math.abs(signedCm(step)), pumpRate))}</td>
                          <td>{levels[i] === undefined ? "—" : formatCm(levels[i], 2)}</td>
                          <td>{offsetText(timeline.starts[i])}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </More>
            )}
            <More label="Timing and capture plan">
              <p className="field-caption" style={{ margin: 0 }}>
                {duration(preset.baseline_s ?? 0)} to record the start, then {pacingText(preset, limits)};{" "}
                {duration(preset.final_dwell_s ?? 0)} to record the end.
              </p>
              {target && (
                <p className="field-caption" style={{ marginTop: 0 }}>
                  Each step aims at a level on the ruler. When it is due it is commanded from the level the step before it
                  settled at, so errors do not add up; a step that settles within ±{tolerance} cm of its target hits it, a
                  miss is flagged and the run goes on. A step already within the pump's resolution of its target is done
                  without pumping. The last target is the start level as measured at the baseline.
                </p>
              )}
              {capture && (
                <p className="field-caption" style={{ marginTop: 0 }}>
                  Capture plan "{preset.capture_plan}": node settings{" "}
                  {Object.entries(capture.settings || {})
                    .map(([k, v]) => `${k} ${v}`)
                    .join(", ") || "unchanged"}
                  {capture.live?.live_s
                    ? `; live mode every ${capture.live.live_s} s for up to ${capture.live.live_min ?? "?"} min${capture.live.keep ? ", kept on" : ""}`
                    : ""}
                  . Restored after the lab run (only the settings still holding these values).
                  {Object.keys(capture.requires || {}).length > 0 &&
                    ` Needs the node to report ${Object.entries(capture.requires)
                      .map(([k, v]) => `${k} ${v} or more`)
                      .join(", ")}${capture.why ? ` (${capture.why})` : ""}.`}
                </p>
              )}
            </More>
            {blockers.length > 0 && (
              <div className="field-notice" role="status">
                <b>Cannot start now:</b>
                <ul className="field-lab-problems">
                  {blockers.map((reason) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="field-lab-input" style={{ marginTop: 0 }}>
              <label htmlFor="lab-notes">Notes (optional)</label>
              <textarea
                id="lab-notes"
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Anything unusual about the tank or the node"
              />
            </div>
            {!asking ? (
              <div className="field-actions" style={{ marginTop: 0 }}>
                <button
                  type="button"
                  className="field-button primary"
                  disabled={!canStart}
                  onClick={() => setAsking(true)}
                >
                  <Play size={15} /> Start lab run
                </button>
              </div>
            ) : (
              <div
                className="field-radar-confirm"
                role="group"
                aria-label="Confirm the lab run"
              >
                <p>
                  <b>
                    Start "{preset.label || preset.key}"{target && parts > 1 ? `, part ${chosenPart} of ${parts},` : ""} from{" "}
                    {start === null ? "the derived level" : formatCm(start)}?
                  </b>
                </p>
                <ul className="field-lab-problems">
                  {startSummary({ ...preset, steps }, capture, status?.node, {
                    limits,
                    part: chosenPart,
                    band: bandChanged ? bandRead.band : null,
                    planned: !unplanned,
                  }).map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
                <div className="field-actions" style={{ marginTop: 10 }}>
                  <button
                    type="button"
                    className="field-button primary"
                    disabled={!canStart}
                    onClick={confirmStart}
                  >
                    {busy ? "Starting…" : "Confirm: start the lab run"}
                  </button>
                  <button
                    type="button"
                    className="field-button"
                    disabled={busy}
                    onClick={() => setAsking(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            {error && (
              <div className="field-alert" role="alert" style={{ marginBottom: 0 }}>
                {error.message}
                {error.problems.length > 0 && (
                  <ul className="field-lab-problems">
                    {error.problems.map((p) => (
                      <li key={p}>{p}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
